import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Scenario, SolutionLink } from '../../../data/referenceGuides/types';
interface ThrulineFitBlockProps {
  intro: string;
  scenarios: Scenario[];
  solutions: SolutionLink[];
  /** Fired when a reader clicks through to a solution page. */
  onSolutionClick?: (to: string) => void;
}

/**
 * The one promotional block in a guide, and the only place Thruline is pitched.
 * The validator enforces that it appears exactly once and lives in the final
 * section — that constraint is what keeps the rest of the guide credible.
 *
 * Design review trimmed its job to one thing: map the reader's current state to
 * the next relevant step. The feature inventory that used to sit here lives on
 * the solution pages, where it belongs.
 */
function FlowDiagram({
  scenario
}: {
  scenario: Scenario;
}) {
  const {
    flow,
    flowConnectors
  } = scenario;
  if (!flow) return null;
  return <ol className="flex flex-col md:flex-row md:items-stretch gap-3 md:gap-2 list-none" data-id="element-328" data-mp="2b88r5qtj3mys">
      {flow.map((node, i) => <li key={node.title} className="flex flex-col md:flex-row md:items-center flex-1 gap-3 md:gap-2" data-id="element-329" data-mp="2kpn5zbk4vblx" data-mpi={i}>
          <div className={`flex-1 rounded-xl border p-5 text-center ${node.owned ? 'border-violet-link/30 bg-violet-link/[0.08]' : 'border-white/[0.08] bg-white/[0.03]'}`} data-id="element-330" data-mp="2pozqvqrg28lv">
            <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${node.owned ? 'text-violet-link' : 'text-white/50'}`} data-id="element-331" data-mp="2ulqgb4p52gjy">
              {node.label}
            </p>
            <p className="text-white font-semibold text-sm mb-1.5" data-id="element-332" data-mp="21znk0xjlacmq">{node.title}</p>
            <p className="text-white/55 text-sm leading-snug" data-id="element-333" data-mp="26x78srdwy7sx">{node.body}</p>
          </div>
          {i < flow.length - 1 && <span aria-hidden="true" className="text-white/30 text-lg text-center md:px-1 rotate-90 md:rotate-0" data-id="element-334" data-mp="2bqi887gebmat">
              {flowConnectors?.[i] === 'next' ? '→' : '⇄'}
            </span>}
        </li>)}
    </ol>;
}
export function ThrulineFitBlock({
  intro,
  scenarios,
  solutions,
  onSolutionClick
}: ThrulineFitBlockProps) {
  return <div className="rounded-2xl border border-violet-link/20 bg-violet-link/[0.04] p-6 sm:p-8 md:p-10 relative overflow-hidden" data-id="element-335" data-mp="1b5luhe1o28mm">
      <div aria-hidden="true" className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-violet-electric to-magenta-hot" data-id="element-336" data-mp="1kyb3m29qvaes" />

      <p className="text-white/60 text-base leading-relaxed mb-10 max-w-2xl" data-id="element-337" data-mp="1fz6t98b336mv">{intro}</p>

      <div className="space-y-12" data-id="element-338" data-mp="s9vo7empaq2x">
        {scenarios.map(s => <section key={s.n} data-id="element-339" data-mp="na101iub8elk">
            <div className="flex items-start gap-4 mb-5" data-id="element-340" data-mp="nb2l8g51ihh0">
              <span className="w-9 h-9 rounded-lg bg-violet-link/20 text-violet-link text-sm font-bold flex items-center justify-center flex-shrink-0" data-id="element-341" data-mp="127a49d4ppcup">
                {s.n}
              </span>
              <div data-id="element-342" data-mp="xajokqwtkj9t">
                <p className="text-white/50 text-sm mb-1" data-id="element-343" data-mp="2dm6xz0bucq8e">{s.condition}</p>
                <h3 className="text-white text-lg font-semibold leading-snug" data-id="element-344" data-mp="2do9wk8r27roq">{s.headline}</h3>
              </div>
            </div>

            <div className="md:pl-13 space-y-7" data-id="element-345" data-mp="2g6bw7xdv4h4h">
              <p className="text-white/60 text-base leading-relaxed max-w-2xl" data-id="element-346" data-mp="2g5k2yt7qmvhd">{s.body}</p>

              <FlowDiagram scenario={s} data-id="element-347" data-mp="2l79dm7r1t74x" />

              {s.footnote && <p className="text-white/50 text-sm leading-relaxed max-w-2xl" data-id="element-348" data-mp="276yl70a3bkhn">{s.footnote}</p>}

              {s.features && <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-349" data-mp="2b8tkjz8gnnss">
                  {s.features.map(f => <div key={f.title} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5" data-id="element-350" data-mp="2yx32gq2jzren">
                      <p className="text-white font-semibold text-sm mb-1.5" data-id="element-351" data-mp="33veen7gbme01">{f.title}</p>
                      <p className="text-white/55 text-sm leading-relaxed" data-id="element-352" data-mp="2q5ub3zex1cpo">{f.body}</p>
                    </div>)}
                </div>}
            </div>
          </section>)}
      </div>

      {solutions.length > 0 && <div className="mt-10 pt-8 border-t border-white/[0.08] flex flex-wrap gap-3" data-id="element-353" data-mp="3hv5vrh24wai9">
          {solutions.map(s => <Link key={s.to} to={s.to} onClick={() => onSolutionClick?.(s.to)} className="inline-flex items-center gap-2 min-h-[44px] px-5 py-2.5 rounded-full bg-white/[0.06] border border-white/15 text-white/85 text-sm font-medium hover:bg-white/[0.1] hover:text-white transition-colors" data-id="element-354" data-mp="3mnqmg9pqu7hk">
              {s.label}
              <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-355" data-mp="1p0ptee8joqlw" />
            </Link>)}
        </div>}
    </div>;
}